import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentCallback() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | paid | pending | error
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!orderId) {
      setStatus("error");
      setErr("Missing order reference.");
      return;
    }
    api.get(`/payment/verify/${orderId}`)
      .then(async (r) => {
        if (r.data.has_paid) {
          await refreshUser();
          setStatus("paid");
        } else {
          setStatus("pending");
        }
      })
      .catch((e) => {
        setStatus("error");
        setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-xl mx-auto px-4 py-16">
        <div className="card-brutal p-8 text-center">
          {status === "checking" && (
            <>
              <Loader2 className="animate-spin mx-auto" size={32} />
              <p className="label-mono mt-4">Verifying your payment…</p>
            </>
          )}

          {status === "paid" && (
            <>
              <CheckCircle2 size={40} className="mx-auto text-green-600" strokeWidth={2.5} />
              <h1 className="font-display text-2xl font-extrabold mt-4">Payment successful!</h1>
              {user?.grade && user?.education_board && user?.school_name ? (
                <>
                  <p className="text-sm text-[#52525B] mt-2">You're all set — let's start your assessment.</p>
                  <button data-testid="payment-continue" onClick={() => nav("/assessment")} className="btn-brutal bg-blue-600 text-white mt-6 px-6 py-3">
                    Start assessment
                  </button>
                </>
              ) : (
                <>
                  <p className="text-sm text-[#52525B] mt-2">Just a few details before we start your assessment.</p>
                  <button data-testid="payment-continue" onClick={() => nav("/register")} className="btn-brutal bg-blue-600 text-white mt-6 px-6 py-3">
                    Create your account
                  </button>
                </>
              )}
            </>
          )}

          {status === "pending" && (
            <>
              <XCircle size={40} className="mx-auto text-amber-600" strokeWidth={2.5} />
              <h1 className="font-display text-2xl font-extrabold mt-4">Payment not completed</h1>
              <p className="text-sm text-[#52525B] mt-2">It looks like the payment wasn't finished. No charge was made.</p>
              <Link to="/pay" className="btn-brutal bg-white mt-6 inline-block px-6 py-3">Try again</Link>
            </>
          )}

          {status === "error" && (
            <>
              <XCircle size={40} className="mx-auto text-red-600" strokeWidth={2.5} />
              <h1 className="font-display text-2xl font-extrabold mt-4">Couldn't verify payment</h1>
              <p className="text-sm text-[#52525B] mt-2">{err}</p>
              <Link to="/pay" className="btn-brutal bg-white mt-6 inline-block px-6 py-3">Back to payment</Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
